/* eslint-disable react/jsx-no-undef */
import { useCallback, useEffect, useMemo } from 'react';
import { Field as FinalFormField, useForm, useFormState } from 'react-final-form';
import { Option, RenderIf } from 'react-rainbow-components';
import { PicklistValue } from '@rainbow-modules/listview/lib/components/AuditLogs/types';
import useCustomer from 'data/firestore/agent/customer/use';
import { validateNotEmpty } from 'data/validators';
import {
    InvoiceBillToCustomField,
    InvoiceBuilderTemplate,
} from '../types';
import {
    billToDefaultValues,
    feeAmountMinor,
    formatMinor,
    lineTotalMinor,
    dollarsToMinor,
    toIsoDate,
    orderedBillToFields,
    orderedColumns,
} from '../helpers';
import { useInvoiceTemplates } from '../api/hooks';
import LineItemsInput, { emptyLine, isInvalidLine, LineDraft } from './LineItemsInput';
import StatusPicker from './StatusPicker';
import {
    FeeAmount,
    FeeLabel,
    FeeList,
    FeeRow,
    FieldSectionTitle,
    BillToFieldsRow,
    FieldsSection,
    FormRow,
    FormSection,
    NoteFields,
    StyledCustomerPicker,
    StyledDatePicker,
    StyledInput,
    StyledPicklist,
    StyledTextarea,
    TemplateEmpty,
} from './styled';

export { emptyLine };
export type { LineDraft };

const Field = (props: any) => <FinalFormField parse={(value) => value} {...props} />;

const validateDueOn = (value: Date, allValues: Record<string, unknown>) => {
    const required = validateNotEmpty('This field is required')(value);
    if (required) return required;
    const due = toIsoDate(value);
    const issued = toIsoDate(allValues.issuedOn as Date);
    if (due && issued && due < issued) {
        return 'Due date must be on or after the issue date.';
    }
    return undefined;
};

const validateLines = (value: LineDraft[], allValues: Record<string, unknown>) => {
    const template = allValues.template as InvoiceBuilderTemplate | undefined;
    if (!template) return undefined;
    const columns = orderedColumns(template.design.columns || []);
    const lines = value || [];
    if (lines.length === 0) return 'This field is required';
    const invalid = lines.some((line) => isInvalidLine(line, columns));
    return invalid ? 'This field is required' : undefined;
};

interface BillToCustomFieldProps {
    field: InvoiceBillToCustomField;
}

const BillToCustomField = ({ field }: BillToCustomFieldProps) => {
    const name = `billToCustomValues.${field.id}`;
    const validate = field.isRequired
        ? validateNotEmpty('This field is required')
        : undefined;

    if (field.valueKind === 'boolean') {
        return (
            <Field name={name} validate={validate}>
                {({ input, meta }: any) => (
                    <StyledPicklist
                        label={field.label}
                        labelAlignment="left"
                        required={field.isRequired}
                        placeholder="Select"
                        value={
                            input.value
                                ? {
                                    name: input.value,
                                    label: input.value === 'true' ? 'Yes' : 'No',
                                }
                                : undefined
                        }
                        onChange={(next: PicklistValue) => (
                            input.onChange(String(next?.name || ''))
                        )}
                        error={meta.touched && meta.error ? meta.error : undefined}
                    >
                        <Option name="true" label="Yes" />
                        <Option name="false" label="No" />
                    </StyledPicklist>
                )}
            </Field>
        );
    }
    if (field.valueKind === 'date') {
        return (
            <Field
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                component={StyledDatePicker}
                name={name}
                label={field.label}
                labelAlignment="left"
                required={field.isRequired}
                validate={validate}
            />
        );
    }
    return (
        <Field
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            component={StyledInput}
            name={name}
            label={field.label}
            labelAlignment="left"
            type={field.valueKind === 'number' ? 'number' : 'text'}
            borderRadius="semi-square"
            required={field.isRequired}
            validate={validate}
        />
    );
};

interface FieldsProps {
    organizationId: string;
    lockedCustomerId?: string;
    mode?: 'create' | 'edit';
}

const Fields = ({
    organizationId,
    lockedCustomerId,
    mode = 'create',
}: FieldsProps) => {
    const { change } = useForm();
    const { values } = useFormState();
    const {
        data: templatesData,
        isLoading: isLoadingTemplates,
    } = useInvoiceTemplates(organizationId);

    const templates = useMemo(
        () => (templatesData?.data.templates || []).filter((template) => template.isActive),
        [templatesData],
    );
    const selectedTemplate = values.template as InvoiceBuilderTemplate | undefined;
    const columns = useMemo(
        () => orderedColumns(selectedTemplate?.design.columns || []),
        [selectedTemplate],
    );
    const billToFields = useMemo(
        () => orderedBillToFields(selectedTemplate?.design.billToFields || []),
        [selectedTemplate],
    );
    const { data: lockedCustomer } = useCustomer(
        organizationId,
        lockedCustomerId || '',
        { disabled: !lockedCustomerId },
    );
    const currency = selectedTemplate?.design.regional.currency || 'USD';
    const locale = selectedTemplate?.design.regional.locale || 'en-US';
    const fees = selectedTemplate?.design.fees || [];
    const subtotalMinor = useMemo(() => (
        ((values.lines as LineDraft[]) || []).reduce((sum, line) => (
            sum + lineTotalMinor(line.quantity, dollarsToMinor(line.rate))
        ), 0)
    ), [values.lines]);
    const selectedTemplateValue = useMemo(() => (
        selectedTemplate
            ? {
                name: selectedTemplate.id,
                label: selectedTemplate.name,
                value: selectedTemplate,
            }
            : undefined
    ), [selectedTemplate]);

    const applyTemplate = useCallback((template: InvoiceBuilderTemplate) => {
        change('template', template);
        change('lines', [emptyLine(orderedColumns(template.design.columns || []))]);
        change('billToCustomValues', billToDefaultValues(template.design.billToFields));
    }, [change]);

    useEffect(() => {
        if (mode === 'edit' || selectedTemplate || templates.length !== 1) return;
        applyTemplate(templates[0]);
    }, [applyTemplate, mode, selectedTemplate, templates]);

    useEffect(() => {
        if (!lockedCustomer) return;
        change('customer', lockedCustomer);
    }, [change, lockedCustomer]);

    return (
        <FieldsSection>
            <Field
                name="template"
                validate={validateNotEmpty('This field is required')}
            >
                {({ input, meta }: any) => (
                    <StyledPicklist
                        label="Template"
                        labelAlignment="left"
                        required
                        placeholder="Select a template"
                        isLoading={isLoadingTemplates}
                        disabled={mode === 'edit'}
                        value={selectedTemplateValue}
                        onChange={(next: PicklistValue) => {
                            const nextTemplate = next?.value as (
                                InvoiceBuilderTemplate | undefined
                            );
                            if (
                                !nextTemplate
                                || nextTemplate.id === selectedTemplate?.id
                            ) {
                                return;
                            }
                            applyTemplate(nextTemplate);
                            input.onChange(nextTemplate);
                        }}
                        error={meta.touched && meta.error ? meta.error : undefined}
                    >
                        {templates.map((template) => (
                            <Option
                                key={template.id}
                                name={template.id}
                                label={template.name}
                                value={template}
                            />
                        ))}
                    </StyledPicklist>
                )}
            </Field>
            <FormSection>
                <FieldSectionTitle>Invoice Details</FieldSectionTitle>
                <FormRow $columns={3}>
                    <Field
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        component={StyledDatePicker}
                        name="issuedOn"
                        label="Issue Date"
                        labelAlignment="left"
                        required
                        validate={validateNotEmpty('This field is required')}
                    />
                    <Field
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        component={StyledDatePicker}
                        name="dueOn"
                        label="Due Date"
                        labelAlignment="left"
                        required
                        minDate={values.issuedOn}
                        validate={validateDueOn}
                    />
                    <Field
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        component={StatusPicker}
                        name="status"
                        label="Status"
                        placeholder="Select status"
                        required
                        remember={mode !== 'edit'}
                    />
                </FormRow>
            </FormSection>
            <FormSection>
                <FieldSectionTitle>Bill To</FieldSectionTitle>
                <Field
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    component={StyledCustomerPicker}
                    agentId={organizationId}
                    name="customer"
                    label="Customer"
                    labelAlignment="left"
                    placeholder="Select a customer"
                    borderRadius="semi-square"
                    enableEdit
                    compact
                    disabled={mode === 'edit' || Boolean(lockedCustomerId)}
                    required
                    validate={
                        lockedCustomerId
                            ? undefined
                            : validateNotEmpty('This field is required')
                    }
                />
                <RenderIf isTrue={billToFields.length > 0}>
                    <BillToFieldsRow>
                        {billToFields.map((field) => (
                            <BillToCustomField key={field.id} field={field} />
                        ))}
                    </BillToFieldsRow>
                </RenderIf>
            </FormSection>
            <RenderIf isTrue={!selectedTemplate}>
                <TemplateEmpty>
                    Select a template to configure invoice fields and line items.
                </TemplateEmpty>
            </RenderIf>
            <RenderIf isTrue={Boolean(selectedTemplate)}>
                <FormSection>
                    <FieldSectionTitle>Items</FieldSectionTitle>
                    <Field
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        component={LineItemsInput}
                        name="lines"
                        columns={columns}
                        currency={currency}
                        locale={locale}
                        validate={validateLines}
                    />
                </FormSection>
                <FormSection>
                    <FieldSectionTitle>Summary</FieldSectionTitle>
                    <FeeList>
                        <FeeRow>
                            <FeeLabel>Subtotal</FeeLabel>
                            <FeeAmount>
                                {formatMinor(subtotalMinor, currency, locale)}
                            </FeeAmount>
                        </FeeRow>
                        {fees.map((fee) => {
                            const rate = fee.calculation.type === 'percentage'
                                ? fee.calculation.rateBasisPoints / 100
                                : null;
                            return (
                                <FeeRow key={fee.id}>
                                    <FeeLabel>
                                        {rate === null ? fee.label : `${fee.label} (${rate}%)`}
                                    </FeeLabel>
                                    <FeeAmount>
                                        {formatMinor(
                                            feeAmountMinor(fee, subtotalMinor),
                                            currency,
                                            locale,
                                        )}
                                    </FeeAmount>
                                </FeeRow>
                            );
                        })}
                        <FeeRow $total>
                            <FeeLabel>Total</FeeLabel>
                            <FeeAmount>
                                {formatMinor(
                                    subtotalMinor + fees.reduce(
                                        (sum, fee) => sum + feeAmountMinor(fee, subtotalMinor),
                                        0,
                                    ),
                                    currency,
                                    locale,
                                )}
                            </FeeAmount>
                        </FeeRow>
                    </FeeList>
                </FormSection>
                <FormSection>
                    <FieldSectionTitle>Notes</FieldSectionTitle>
                    <NoteFields>
                        <Field
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                            component={StyledTextarea}
                            name="notes"
                            label="Invoice Note"
                            labelAlignment="left"
                            placeholder="Visible to the customer on the invoice"
                            borderRadius="semi-square"
                            rows={4}
                        />
                        <Field
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                            component={StyledTextarea}
                            name="internalNote"
                            label="Internal Note"
                            labelAlignment="left"
                            placeholder="Only visible to your team, not shown on the invoice"
                            borderRadius="semi-square"
                            rows={4}
                        />
                    </NoteFields>
                </FormSection>
            </RenderIf>
        </FieldsSection>
    );
};

export default Fields;
